import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Feature, FeatureCollection } from 'geojson';
import debounce from 'lodash.debounce';

import {
  DRAWING_Z_INDEX,
  EDIT_COMMIT_MS,
  LINE_COLOR,
  STROKE_WEIGHT,
} from '../drawingConstants';
import { CommittedShape, pathToLiterals } from '../drawingUtilities';
import {
  circleToFeature,
  DrawingMode,
  pathToLineFeature,
  pathToPolygonFeature,
} from '../geometry';

export type EditableShape = {
  // The currently committed shape; draw hooks adopt their finished overlay by
  // writing to this ref, then call makeEditable.
  committedRef: RefObject<CommittedShape | null>;
  // Turn a finished overlay into a natively editable one and wire its reshape
  // events to a debounced commit.
  makeEditable: (shape: CommittedShape['shape'], kind: DrawingMode) => void;
  // Emit immediately, dropping any pending debounced edit commit (draw gestures
  // finish with final geometry, so they skip the debounce).
  commitNow: (feature: Feature) => void;
  // Read the live committed overlay back into a GeoJSON feature.
  overlayToFeature: () => Feature | null;
  // Tear down the committed shape and its edit listeners.
  destroyShape: () => void;
  // Hydrate an existing GeoJSON feature as the committed, editable shape.
  seedShape: (
    map: google.maps.Map,
    feature: Feature,
    lineColor?: string,
  ) => void;
};

// Owns the lifecycle of the layer's single committed shape: reading it back to
// GeoJSON, the debounced edit-commit machinery, and making it natively editable.
const useEditableShape = (
  onChange: (next: FeatureCollection) => void,
): EditableShape => {
  // Latest onChange without re-subscribing effects on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const committedRef = useRef<CommittedShape | null>(null);
  const editListenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const overlayToFeature = useCallback((): Feature | null => {
    const committed = committedRef.current;
    if (!committed) {
      return null;
    }

    switch (committed.kind) {
      case 'circle': {
        const center = committed.shape.getCenter();
        return center
          ? circleToFeature(
              { lat: center.lat(), lng: center.lng() },
              committed.shape.getRadius(),
            )
          : null;
      }
      case 'polygon':
        return pathToPolygonFeature(pathToLiterals(committed.shape.getPath()));
      case 'linestring':
        return pathToLineFeature(pathToLiterals(committed.shape.getPath()));
      default:
        return null;
    }
  }, []);

  const emit = useCallback((feature: Feature) => {
    onChangeRef.current({ type: 'FeatureCollection', features: [feature] });
  }, []);

  // Debounced commit for the editable lifecycle: native resize/move events fire
  // continuously while dragging a handle or vertex, so collapse the burst into a
  // single emit once the user pauses. Re-reads the live overlay at fire time so
  // the committed feature reflects the final geometry, not a mid-drag snapshot.
  const scheduleCommit = useMemo(
    () =>
      debounce(() => {
        const feature = overlayToFeature();
        if (feature) {
          emit(feature);
        }
      }, EDIT_COMMIT_MS),
    [emit, overlayToFeature],
  );

  // Cancel any pending debounced commit when it is replaced or the layer
  // unmounts, so a trailing edit can't fire against a torn-down shape.
  useEffect(() => () => scheduleCommit.cancel(), [scheduleCommit]);

  // Immediate commit when a draw gesture finishes — the geometry is final, so
  // skip the debounce and drop any edit commit still pending from a prior shape.
  const commitNow = useCallback(
    (feature: Feature) => {
      scheduleCommit.cancel();
      emit(feature);
    },
    [emit, scheduleCommit],
  );

  // A committed shape is editable for its whole life: native resize/move
  // handles plus a debounced commit on every reshape. Lines are editable only;
  // circles/polygons also drag-to-move.
  const makeEditable = useCallback(
    (shape: CommittedShape['shape'], kind: DrawingMode) => {
      if (kind === 'circle') {
        const circle = shape as google.maps.Circle;
        // clickable must be true for the native edit handles to receive events.
        circle.setOptions({ editable: true, draggable: true, clickable: true });
        editListenersRef.current = [
          circle.addListener('radius_changed', scheduleCommit),
          circle.addListener('center_changed', scheduleCommit),
        ];
        return;
      }
      // Polygon / polyline: clickable must be true or the vertex + midpoint
      // handles can't be grabbed. Polygons also drag-to-move; lines are
      // editable only.
      const editable = shape as google.maps.Polygon | google.maps.Polyline;
      editable.setOptions({
        editable: true,
        clickable: true,
        draggable: kind === 'polygon',
      });

      const path = editable.getPath();
      editListenersRef.current = [
        path.addListener('set_at', scheduleCommit),
        path.addListener('insert_at', scheduleCommit),
        path.addListener('remove_at', scheduleCommit),
        editable.addListener('dragend', scheduleCommit),
      ];
    },
    [scheduleCommit],
  );

  const destroyShape = useCallback(() => {
    editListenersRef.current.forEach(listener => listener.remove());
    editListenersRef.current = [];
    committedRef.current?.shape.setMap(null);
    committedRef.current = null;
  }, []);

  // Hydrate an EXISTING GeoJSON feature as the committed, editable shape.
  const seedShape = useCallback(
    (map: google.maps.Map, feature: Feature, lineColor?: string) => {
      const { geometry } = feature;

      if (geometry.type === 'LineString') {
        const path = geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
        const polyline = new google.maps.Polyline({
          map,
          path,
          strokeColor: lineColor ?? LINE_COLOR,
          strokeWeight: STROKE_WEIGHT,
          zIndex: DRAWING_Z_INDEX,
        });

        committedRef.current = { kind: 'linestring', shape: polyline };
        makeEditable(polyline, 'linestring');
      }
    },
    [makeEditable],
  );

  return {
    committedRef,
    makeEditable,
    commitNow,
    overlayToFeature,
    destroyShape,
    seedShape,
  };
};

export default useEditableShape;
